import React from "react";
import { Isvg, Spinner } from "../../../components";
import { NavigatorAction, LibraryStore, Dispatcher } from "../../../flux";
import { Button } from "@vitalsource/vstui";
import { PlusCircleFill } from "@vitalsource/vstui-icons";
import Dropdown from "../dropdown";
import IntlUtil from "../../../../../utils/IntlUtils";

export default class Assignments extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      fetching: false,
      books: {},
      assignments: {},
      toCancel: []
    };
    this._fractionStoreChanged = this._fractionStoreChanged.bind(this);
    this._copyLinkModal = this._copyLinkModal.bind(this);
    this._libraryUpdated = this._libraryUpdated.bind(this);
    this.rawAssignmentData = [];
  }

  componentDidMount() {
    LibraryStore.loadBookList();
    this._loadBooks();
    Dispatcher.setMaxListeners(Dispatcher.getMaxListeners() + 2);
    Dispatcher.bind(
      Dispatcher.FRACTION_STORE_CHANGE,
      this._fractionStoreChanged
    );
    Dispatcher.bind(Dispatcher.LIBRARY_UPDATED, this._libraryUpdated);
  }

  componentWillUnmount() {
    this.state.toCancel.forEach(cancel => cancel());
    Dispatcher.unbind(
      Dispatcher.FRACTION_STORE_CHANGE,
      this._fractionStoreChanged
    );
    Dispatcher.unbind(Dispatcher.LIBRARY_UPDATED, this._libraryUpdated);
    Dispatcher.setMaxListeners(Dispatcher.getMaxListeners() - 2);
  }

  _loadBooks() {
    const toCancel = this.state.toCancel;
    const feenyInit = Feeny.init({ cancelable: true });
    toCancel.push(feenyInit.cancel.bind(feenyInit));
    this.setState({ toCancel, fetching: true });
    feenyInit.promise
      .then(() => Feeny.Assignments.all())
      .then(assignments => {
        this.rawAssignmentData = assignments.data;
        this._configureState();
      })
      .catch(e => Fraction.Helpers.removeModal());
  }

  _fractionStoreChanged() {
    this.forceUpdate();
  }

  _libraryUpdated() {
    this._configureState();
  }

  _configureState() {
    const books = LibraryStore.get().bookList.reduce((books, book) => {
      book.assignments = [];
      book.activeAssignment = null;
      let newState = {};
      newState[book.isbn] = book;
      return Object.assign(books, newState);
    }, {});
    const assignments = this.rawAssignmentData.reduce(
      (assignments, assignment) => {
        if (books.hasOwnProperty(assignment.vbid)) {
          assignment.book = Fraction.Helpers.getBookInformation(
            assignment.vbid
          );
          assignments[assignment.uuid] = assignment;
          books[assignment.vbid].assignments.push(assignment);
        }
        return assignments;
      },
      {}
    );

    const booksWithAssignments = Object.keys(books)
      .filter(isbn => books[isbn].assignments.length > 0)
      .reduce((b, isbn) => {
        let newState = {};
        newState[isbn] = books[isbn];
        return Object.assign(b, newState);
      }, {});
    this.setState({
      books: booksWithAssignments,
      assignments,
      fetching: false
    });
  }

  _clickBook(event, isbn) {
    Fraction.Reporter.gaEvent("V3/books", isbn, "openBook");
  }

  _deleteAssignment(assignment) {
    Feeny.Assignments.delete(assignment.uuid).then(e => {
      const books = this.state.books;
      const removeIndex = books[assignment.vbid].assignments.indexOf(
        assignment
      );
      books[assignment.vbid].assignments.splice(removeIndex, 1);
      this.setState({ books }, () =>
        Fraction.Helpers.removeModal(() => this.createNewButton.focus())
      );
    });
  }

  _deleteAssignmentModal(e, assignmentId) {
    e.stopPropagation();
    const assignment = this.state.assignments[assignmentId];
    Fraction.Helpers.showModal(
      {
        title: `${i18n.t("base.delete")} '${assignment.title}'?`,
        content: <div>{i18n.t("assignments.delete-assignment")}</div>,
        buttons: [
          {
            label: i18n.t("base.cancel"),
            click: Fraction.Helpers.removeModal,
            color: "gray"
          },
          {
            label: i18n.t("base.delete"),
            click: this._deleteAssignment.bind(this, assignment)
          }
        ]
      },
      this.deleteButton
    );
  }

  _copyLink(url) {
    Fraction.Helpers.copyText(url);
    Fraction.Helpers.toast(i18n.t("assignments.copied"), { centered: true });
    Fraction.Helpers.removeModal();
  }

  _copyLinkModal(e, assignment) {
    e.stopPropagation();
    const url = `${window.location.origin}/#/books/${
      assignment.vbid
    }/assignment/${assignment.uuid}`;
    Fraction.Helpers.showModal(
      {
        title: `${i18n.t("base.copy")} '${assignment.title}' URL`,
        content: (
          <div className="citations link">
            <div className="citation-output">{url}</div>
          </div>
        ),
        fullscreen: false,
        width: 480,
        buttons: [
          { label: i18n.t("base.copy"), click: this._copyLink.bind(this, url) }
        ]
      },
      this.copyButton
    );
  }

  _toggleReadings(assignment) {
    const books = this.state.books;
    if (books && books[assignment.vbid]) {
      const isActiveAssignment =
        books[assignment.vbid].activeAssignment === assignment.uuid;
      if (
        isActiveAssignment ||
        (assignment.readings && !assignment.readings.length)
      ) {
        books[assignment.vbid].activeAssignment = null;
      } else {
        books[assignment.vbid].activeAssignment = assignment.uuid;
      }
      this.setState({ books });
    }
  }

  formatDate(assignment) {
    // NOTE Safari don't support the format "00-00-00 00:00:00"
    // Checking for global.Intl because of safari 9
    // Fall back to original date format if non-existant
    const d = assignment.created_at.split(/[^0-9]/);
    const date = new Date(d[0], d[1] - 1, d[2], d[3], d[4], d[5]);
    return IntlUtil(date, Fraction.User.locale);
  }

  renderAssignmentRow(assignment) {
    const expanded =
      this.state.books[assignment.vbid].activeAssignment === assignment.uuid;
    return (
      <div className="assignment">
        <button
          className="noButton"
          aria-expanded={expanded}
          onClick={this._toggleReadings.bind(this, assignment)}
        >
          <div className="assignment-content">
            <Isvg
              src="/img/next_arrow.svg"
              style={{ display: expanded ? "none" : "inline" }}
            />
            <Isvg
              src="/img/down_arrow.svg"
              style={{ display: !expanded ? "none" : "inline" }}
            />
            <div className="assignment-title">{assignment.title}</div>
          </div>
          <div className="assignment-date">{this.formatDate(assignment)}</div>
        </button>
        {this.renderReadings(assignment)}
        <button
          className="assignment-actions noButton three-dot-button"
          aria-expanded={this.props.active === assignment.uuid}
          onClick={e => this.props.showActions(e, assignment.uuid)}
        >
          <Isvg src="/img/assignments/assets/img/three-dots-menu.svg" />
          <span className="offscreen">{i18n.t("assignments.options")}</span>
        </button>
        {this.renderDropDown(assignment)}
      </div>
    );
  }

  renderReadings(assignment) {
    const book = this.state.books[assignment.vbid];
    if (
      book &&
      book.activeAssignment === assignment.uuid &&
      assignment.readings
    ) {
      return (
        <ul className="readings">
          {assignment.readings.map(reading => (
            <li className="reading" key={reading.uuid}>
              {reading.description || i18n.t("assignments.untitled")}
            </li>
          ))}
        </ul>
      );
    }
  }

  renderAssignment(assignment) {
    return (
      <li className="assignment-row" key={assignment.uuid} id={assignment.uuid}>
        {this.renderAssignmentRow(assignment)}
      </li>
    );
  }

  renderAssignmentList(book) {
    let assignmentCount = book.assignments.length;
    return (
      <div className="assignments-container">
        <div className="summary">
          <span>
            {assignmentCount}{" "}
            {i18n.t(
              `assignments.${
                assignmentCount === 1 ? "assignment" : "assignments"
              }`
            )}
          </span>
          <a
            className="add-assignment noButton"
            href={`#/books/${book.isbn}/assignment/new`}
            onClick={this._clickBook.bind(this, book.isbn)}
          >
            <span className="plus-sign" aria-hidden={true}>
              +
            </span>
            {i18n.t("assignments.add-assignment")}
          </a>
        </div>
        <ul className="assignment-list">
          {book.assignments.map(assignment =>
            this.renderAssignment(assignment)
          )}
        </ul>
      </div>
    );
  }

  renderBooks() {
    const books = Object.keys(this.state.books).map(
      vbid => this.state.books[vbid]
    );
    if (books.length) {
      return books
        .filter(book => book.assignments.length > 0)
        .map(book => (
          <div className="book-assignments" key={book.isbn}>
            <div className="book-container">
              <img
                src={LibraryStore.get()
                  .coverURL.replace(":width", "150")
                  .replace(":isbn", book.isbn)}
                alt=""
              />
              <div className="book-title">
                <h2 dangerouslySetInnerHTML={{ __html: book.title }} />
              </div>
            </div>
            {this.renderAssignmentList(book)}
          </div>
        ));
    }
  }

  renderDropDown(assignment) {
    return (
      <Dropdown
        position="bottomLeft"
        active={this.props.active === assignment.uuid}
      >
        <button
          ref={copy => {
            this.copyButton = copy;
          }}
          className="noButton"
          onClick={e => {
            this._copyLinkModal(e, assignment);
          }}
        >
          <Isvg src="/img/assignments/assets/img/assignments_copy_link.svg" />
          <span>{i18n.t("assignments.copy-link")}</span>
        </button>
        <a
          href={`#/books/${assignment.vbid}/assignment/${assignment.uuid}`}
          target="_blank"
        >
          <Isvg src="/img/assignments/assets/img/assignments_preview.svg" />
          <span>{i18n.t("base.preview")}</span>
        </a>
        <a
          href={`#/books/${assignment.vbid}/assignment/${assignment.uuid}/edit`}
        >
          <Isvg src="/img/assignments/assets/img/assignments_edit.svg" />
          <span>{i18n.t("base.edit")}</span>
        </a>
        <button
          ref={deleteButton => {
            this.deleteButton = deleteButton;
          }}
          className="noButton"
          onClick={e => {
            this._deleteAssignmentModal.call(this, e, assignment.uuid);
          }}
        >
          <Isvg src="/img/assignments/assets/img/assignments_trash.svg" />
          <span>{i18n.t("base.delete")}</span>
        </button>
      </Dropdown>
    );
  }

  render() {
    if (this.state.fetching) {
      return (
        <div id="creator-assignments">
          <Spinner style={{ background: "white" }} />
        </div>
      );
    }
    const hasAssignments = Object.keys(this.state.assignments).length > 0;

    return (
      <div className="outer-actions">
        <div
          className={hasAssignments ? "assignment-actions" : "empty-actions"}
        >
          {!this.state.fetching &&
            !hasAssignments && [
              <h3 key="assignment-empty-header" className="message">
                {i18n.t("assignments.assignments-empty")}
              </h3>,
              <p key="assignment-empty-paragraph">
                {i18n.t("assignments.assignments-empty-paragraph")}
              </p>
            ]}

          <Button
            ref={button => {
              this.createNewButton = button;
            }}
            size="large"
            onClick={() => NavigatorAction.push("library")}
            iconStart={<PlusCircleFill />}
          >
            {!hasAssignments
              ? i18n.t("assignments.get-started")
              : i18n.t("assignments.create-new")}
          </Button>
        </div>
        <div id="creator-assignments">{this.renderBooks()}</div>
        {Fraction.Helpers.modalToShow}
      </div>
    );
  }
}
